/**
 * /payroll-l2/labor-event/:eventId — Labor Event Detail
 *
 * Shows full event detail: face match, geofence, time/allocation,
 * HR responses, flag reasons, override/reject actions, crew edits.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { api } from "@/lib/api";
import type { LaborEvent, LaborEventVerificationStatus } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/labor-event_/$eventId")({
  component: LaborEventDetailPage,
});

const STATUS_COLOR: Record<LaborEventVerificationStatus, "default" | "success" | "warning" | "error" | "info"> = {
  pending: "default",
  verified: "success",
  flagged: "warning",
  overridden: "info",
  rejected: "error",
};

function fmtDt(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

interface CrewEdit {
  id: string;
  labor_event_id: string;
  editor_id: string;
  edit_type: string;
  before_value: string | null;
  after_value: string | null;
  reason: string | null;
  created_at: string;
}

// ─── Face match gauge ─────────────────────────────────────────────────────────

function FaceGauge({ confidence, threshold }: { confidence: number; threshold: number }) {
  const color = confidence >= 90 ? "#22c55e" : confidence >= 80 ? "#f59e0b" : "#ef4444";
  const label = confidence >= 90 ? "High confidence" : confidence >= 80 ? "Moderate confidence" : "Low confidence";
  const size = 100;
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(100, confidence) / 100;
  const dash = pct * circumference;
  // threshold arc
  const thresholdAngle = (threshold / 100) * 360;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Track */}
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#e5e7eb" strokeWidth={10} />
        {/* Fill */}
        <circle
          cx="50" cy="50" r={radius}
          fill="none"
          stroke={color}
          strokeWidth={10}
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
        />
        {/* Threshold tick */}
        <line
          x1="50"
          y1={50 - radius - 4}
          x2="50"
          y2={50 - radius + 4}
          stroke="#64748b"
          strokeWidth={2}
          transform={`rotate(${thresholdAngle - 90} 50 50)`}
        />
        <text x="50" y="54" textAnchor="middle" fontSize="14" fontWeight="bold" fill={color}>
          {Math.round(confidence)}%
        </text>
      </svg>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" color="text.secondary">Threshold: {threshold}%</Typography>
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function LaborEventDetailPage() {
  const { eventId } = Route.useParams();
  const qc = useQueryClient();

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [crewEditOpen, setCrewEditOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [crewEditForm, setCrewEditForm] = useState({
    edit_type: "hours_correction",
    before_value: "",
    after_value: "",
    reason: "",
  });

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "labor-events", eventId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/labor-events/${eventId}`);
      return res as LaborEvent;
    },
  });

  const { data: crewEdits = [] } = useQuery({
    queryKey: ["payroll-l2", "labor-events", eventId, "crew-edits"],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/labor-events/${eventId}/crew-edits`);
      return res as CrewEdit[];
    },
  });

  const overrideMut = useMutation({
    mutationFn: async (override_reason: string) => {
      return api(`/api/payroll-l2/labor-events/${eventId}/override`, {
        method: "POST",
        body: { override_reason },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "labor-events", eventId] });
      setOverrideOpen(false);
      setOverrideReason("");
    },
  });

  const rejectMut = useMutation({
    mutationFn: async (reason: string) => {
      return api(`/api/payroll-l2/labor-events/${eventId}/reject`, {
        method: "POST",
        body: { reason },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "labor-events", eventId] });
      setRejectOpen(false);
      setRejectReason("");
    },
  });

  const crewEditMut = useMutation({
    mutationFn: async (data: typeof crewEditForm) => {
      return api(`/api/payroll-l2/crew-edits`, {
        method: "POST",
        body: { labor_event_id: eventId, ...data },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "labor-events", eventId, "crew-edits"] });
      setCrewEditOpen(false);
      setCrewEditForm({ edit_type: "hours_correction", before_value: "", after_value: "", reason: "" });
    },
  });

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  if (error || !event) return <Box sx={{ p: 3 }}><Alert severity="error">Event not found.</Alert></Box>;

  const confidence = parseFloat(event.face_match_confidence ?? "0") * 100;
  const threshold = parseFloat(event.face_threshold_used ?? "0.85") * 100;
  const lat = parseFloat(event.geo_lat ?? "0");
  const lng = parseFloat(event.geo_lng ?? "0");
  const distanceM = event.geo_distance_meters ? parseFloat(event.geo_distance_meters) : null;
  const insideFence = distanceM === null || distanceM <= 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Link to="/payroll-l2/timesheet-feed">
          <Button startIcon={<ArrowBackIcon />} size="small">Timesheet Feed</Button>
        </Link>
        <Typography variant="body2" color="text.secondary">
          / Event {event.source_event_id}
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>Labor Event</Typography>
          <Chip
            label={event.verification_status}
            color={STATUS_COLOR[event.verification_status]}
            size="small"
          />
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {event.source_event_id}
          </Typography>
        </Box>
        <HelpHint docPath="payroll-l2/labor-event-override.md" label="Labor event help" />
      </Box>

      {/* Flag reasons alert */}
      {event.flag_reasons && event.flag_reasons.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {event.flag_reasons.map((f) => (
              <Chip key={f} label={f} size="small" color="warning" variant="outlined" />
            ))}
          </Box>
        </Alert>
      )}

      {/* Two-column layout */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
        {/* Left: Verification panel */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Verification</Typography>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <FaceGauge confidence={confidence} threshold={threshold} />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Geofence */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            {insideFence ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <ErrorIcon color="error" fontSize="small" />
            )}
            <Typography variant="body2">
              {insideFence
                ? "Inside geofence"
                : `Outside fence by ${Math.round(distanceM ?? 0)}m`}
            </Typography>
          </Box>

          {/* Device/location */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {event.geo_lat && event.geo_lng && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 80 }}>Location</Typography>
                <Typography variant="body2">
                  <a
                    href={`https://www.google.com/maps?q=${lat},${lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit" }}
                  >
                    {lat.toFixed(5)}, {lng.toFixed(5)}
                  </a>
                </Typography>
              </Box>
            )}
            {event.worked_in_state && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 80 }}>Worked In</Typography>
                <Chip label={event.worked_in_state} size="small" variant="outlined" />
              </Box>
            )}
          </Box>
        </Paper>

        {/* Right: Time & allocation */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Time & Allocation</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Employee</Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{event.employee_id.slice(0, 8)}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Clock In</Typography>
              <Typography variant="body2">{fmtDt(event.clock_in)}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Clock Out</Typography>
              <Typography variant="body2">{fmtDt(event.clock_out)}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Duration</Typography>
              <Typography variant="body2">{event.worked_hours ? `${event.worked_hours} hrs` : "—"}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Break</Typography>
              <Typography variant="body2">{event.break_minutes} min</Typography>
            </Box>
            <Divider />
            {event.project_id && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Project</Typography>
                <Chip label={event.project_id.slice(0, 8)} size="small" variant="outlined" />
              </Box>
            )}
            {event.cost_code_id && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Cost Code</Typography>
                <Chip label={event.cost_code_id.slice(0, 8)} size="small" variant="outlined" />
              </Box>
            )}
            {event.craft_id && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Craft</Typography>
                <Chip label={event.craft_id.slice(0, 8)} size="small" variant="outlined" />
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* HR Responses */}
      {event.hr_responses && Object.keys(event.hr_responses).length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>HR Responses</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Question</TableCell>
                <TableCell>Answer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(event.hr_responses).map(([k, v]) => (
                <TableRow key={k}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{k}</TableCell>
                  <TableCell>
                    {typeof v === "object" && v !== null && "answer" in v ? (
                      <Box>
                        <Typography variant="body2">{String((v as Record<string, unknown>).answer)}</Typography>
                        {Boolean((v as Record<string, unknown>).attested_at) && (
                          <Typography variant="caption" color="text.secondary">
                            Attested: {fmtDt(String((v as Record<string, unknown>).attested_at))}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2">{String(v)}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Override / Reject actions */}
      {event.verification_status === "flagged" && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "warning.main" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Actions</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="warning"
              onClick={() => setOverrideOpen(true)}
            >
              Override
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setRejectOpen(true)}
            >
              Reject Event
            </Button>
          </Box>
        </Paper>
      )}

      {/* Override info if already overridden */}
      {event.verification_status === "overridden" && event.override_reason && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Overridden: {event.override_reason}
        </Alert>
      )}

      {/* Reject info if already rejected */}
      {event.verification_status === "rejected" && event.reject_reason && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Rejected: {event.reject_reason}
        </Alert>
      )}

      {/* Crew edits tab */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Crew Edits</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setCrewEditOpen(true)}
          >
            + Foreman Edit
          </Button>
        </Box>

        {crewEdits.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No crew edits recorded.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Editor</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Before</TableCell>
                <TableCell>After</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {crewEdits.map((ce) => (
                <TableRow key={ce.id}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{ce.editor_id.slice(0, 8)}</TableCell>
                  <TableCell><Chip label={ce.edit_type} size="small" /></TableCell>
                  <TableCell sx={{ color: "error.main" }}>{ce.before_value ?? "—"}</TableCell>
                  <TableCell sx={{ color: "success.main" }}>{ce.after_value ?? "—"}</TableCell>
                  <TableCell>{ce.reason ?? "—"}</TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{fmtDt(ce.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Override dialog */}
      <Dialog open={overrideOpen} onClose={() => setOverrideOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Labor Event</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Override Reason (required)"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
          />
          {overrideMut.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{String((overrideMut.error as Error)?.message)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!overrideReason.trim() || overrideMut.isPending}
            onClick={() => overrideMut.mutate(overrideReason.trim())}
          >
            {overrideMut.isPending ? "Saving…" : "Override"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Labor Event</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            This event will be excluded from payroll calculation.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Reason (required)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          {rejectMut.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{String((rejectMut.error as Error)?.message)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!rejectReason.trim() || rejectMut.isPending}
            onClick={() => rejectMut.mutate(rejectReason.trim())}
          >
            {rejectMut.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Crew edit dialog */}
      <Dialog open={crewEditOpen} onClose={() => setCrewEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Foreman Edit</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            select
            label="Edit Type"
            value={crewEditForm.edit_type}
            onChange={(e) => setCrewEditForm((p) => ({ ...p, edit_type: e.target.value }))}
            fullWidth
          >
            {[
              "hours_correction",
              "cost_code_change",
              "craft_change",
              "project_change",
              "break_adjustment",
              "other",
            ].map((t) => (
              <MenuItem key={t} value={t}>{t.replace(/_/g, " ")}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Before Value"
              value={crewEditForm.before_value}
              onChange={(e) => setCrewEditForm((p) => ({ ...p, before_value: e.target.value }))}
            />
            <TextField
              label="After Value"
              value={crewEditForm.after_value}
              onChange={(e) => setCrewEditForm((p) => ({ ...p, after_value: e.target.value }))}
            />
          </Box>
          <TextField
            label="Reason (required)"
            multiline
            rows={2}
            value={crewEditForm.reason}
            onChange={(e) => setCrewEditForm((p) => ({ ...p, reason: e.target.value }))}
          />
          {crewEditMut.isError && (
            <Alert severity="error">{String((crewEditMut.error as Error)?.message)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrewEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!crewEditForm.reason.trim() || crewEditMut.isPending}
            onClick={() => crewEditMut.mutate(crewEditForm)}
          >
            {crewEditMut.isPending ? "Saving…" : "Save Edit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
